<template>
  <div class="compare-container p-4">
    <h2>Product Stats Comparison</h2>
    <hr class="my-2" />

    <!-- Preset Options -->
    <div class="preset-options mb-6">
      <h3 class="text-lg font-semibold mb-3">Quick Compare Options</h3>
      <div class="flex flex-wrap gap-2">
        <button
          @click="compareThisMonthWithPreviousMonth"
          class="btn btn-secondary"
        >
          This Month vs Previous Month
        </button>
        <button @click="compareThisMonthWithLastYear" class="btn btn-secondary">
          This Month vs Same Month Last Year
        </button>
        <button
          @click="compareLastMonthWithPreviousYear"
          class="btn btn-secondary"
        >
          Last Month vs Same Month Last Year
        </button>
        <button
          @click="compareLast6MonthsWithPrevious6"
          class="btn btn-secondary"
        >
          Last 6 Months vs Previous 6 Months
        </button>
        <button @click="compareThisYearWithLastYear" class="btn btn-secondary">
          This Year vs Last Year
        </button>
      </div>
    </div>

    <!-- Custom Date Ranges -->
    <div class="custom-ranges mb-6">
      <h3 class="text-lg font-semibold mb-3">Custom Date Ranges</h3>
      <div class="grid grid-cols-2 gap-6">
        <!-- First Date Range -->
        <div class="border p-4 rounded">
          <h4 class="font-semibold mb-3">Period 1</h4>
          <div class="flex flex-col gap-3">
            <div>
              <label for="fromDate1" class="text-sm font-semibold"
                >From Date</label
              >
              <input
                type="date"
                id="fromDate1"
                v-model="fromDate1"
                class="w-full"
              />
            </div>
            <div>
              <label for="toDate1" class="text-sm font-semibold">To Date</label>
              <input
                type="date"
                id="toDate1"
                v-model="toDate1"
                class="w-full"
              />
            </div>
          </div>
        </div>

        <!-- Second Date Range -->
        <div class="border p-4 rounded">
          <h4 class="font-semibold mb-3">Period 2</h4>
          <div class="flex flex-col gap-3">
            <div>
              <label for="fromDate2" class="text-sm font-semibold"
                >From Date</label
              >
              <input
                type="date"
                id="fromDate2"
                v-model="fromDate2"
                class="w-full"
              />
            </div>
            <div>
              <label for="toDate2" class="text-sm font-semibold">To Date</label>
              <input
                type="date"
                id="toDate2"
                v-model="toDate2"
                class="w-full"
              />
            </div>
          </div>
        </div>
      </div>
      <button @click="loadComparison" class="btn btn-primary mt-4">
        Compare
      </button>
    </div>

    <!-- Results -->
    <div class="results mt-6">
      <div v-if="loading">
        <div class="spinner-border text-primary" role="status">
          <span class="sr-only">Loading...</span>
        </div>
      </div>
      <div v-else-if="comparisonData">
        <div class="flex justify-between items-center mb-4">
          <h3 class="text-lg font-semibold">Comparison Results</h3>
          <a
            v-if="comparisonData"
            :href="
              `${url}/products/stats2_compare?fromDate1=${encodeURIComponent(
                comparisonData.currentPeriod.from
              )}&toDate1=${encodeURIComponent(
                comparisonData.currentPeriod.to
              )}&fromDate2=${encodeURIComponent(
                comparisonData.previousPeriod.from
              )}&toDate2=${encodeURIComponent(
                comparisonData.previousPeriod.to
              )}&token=${encodeURIComponent(token)}`
            "
            class="btn btn-export"
            target="_blank"
          >
            export
          </a>
        </div>

        <!-- Summary Cards -->
        <div class="summary-cards grid grid-cols-2 gap-4 mb-6">
          <div class="summary-card period-1">
            <h4 class="font-semibold text-sm mb-2">Current Period</h4>
            <p class="text-xs text-gray-600">
              {{ comparisonData.currentPeriod.from }} to
              {{ comparisonData.currentPeriod.to }}
            </p>
            <p class="text-2xl font-bold mt-2">
              {{ comparisonData.currentPeriod.totalProductsSold }}
            </p>
            <p class="text-xs text-gray-600">Products Sold</p>
          </div>

          <div class="summary-card period-2">
            <h4 class="font-semibold text-sm mb-2">Previous Period</h4>
            <p class="text-xs text-gray-600">
              {{ comparisonData.previousPeriod.from }} to
              {{ comparisonData.previousPeriod.to }}
            </p>
            <p class="text-2xl font-bold mt-2">
              {{ comparisonData.previousPeriod.totalProductsSold }}
            </p>
            <p class="text-xs text-gray-600">Products Sold</p>
          </div>
        </div>

        <!-- Comparison Table -->
        <div class="table-responsive">
          <table class="comparison-table">
            <thead>
              <tr>
                <th @click="sortTable('productCode')" class="sortable">
                  Product Code
                  <span
                    v-if="sortBy.column === 'productCode'"
                    class="sort-indicator"
                  >
                    {{ sortBy.order === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
                <th @click="sortTable('description')" class="sortable">
                  Description
                  <span
                    v-if="sortBy.column === 'description'"
                    class="sort-indicator"
                  >
                    {{ sortBy.order === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
                <th @click="sortTable('currentPeriod')" class="sortable">
                  Current Period
                  <span
                    v-if="sortBy.column === 'currentPeriod'"
                    class="sort-indicator"
                  >
                    {{ sortBy.order === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
                <th @click="sortTable('previousPeriod')" class="sortable">
                  Previous Period
                  <span
                    v-if="sortBy.column === 'previousPeriod'"
                    class="sort-indicator"
                  >
                    {{ sortBy.order === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
                <th @click="sortTable('change')" class="sortable">
                  Change
                  <span
                    v-if="sortBy.column === 'change'"
                    class="sort-indicator"
                  >
                    {{ sortBy.order === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
                <th @click="sortTable('percentageChange')" class="sortable">
                  % Change
                  <span
                    v-if="sortBy.column === 'percentageChange'"
                    class="sort-indicator"
                  >
                    {{ sortBy.order === "asc" ? "↑" : "↓" }}
                  </span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="item in getSortedData()"
                :key="item.productCode"
                :class="['row-' + item.direction]"
              >
                <td class="product-code">{{ item.productCode }}</td>
                <td class="description">{{ item.description }}</td>
                <td class="number">{{ item.currentPeriod }}</td>
                <td class="number">{{ item.previousPeriod }}</td>
                <td class="number change" :style="{ color: item.color.hex }">
                  {{ item.change > 0 ? "+" : "" }}{{ item.change }}
                </td>
                <td
                  class="number percentage"
                  :style="{ backgroundColor: item.color.hex, color: 'white' }"
                >
                  {{ item.percentageChangeFormatted }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { getProductsStatsCompare } from "@/api/Products";

export default {
  name: "ProductStatsCompare",
  data() {
    return {
      loading: false,
      comparisonData: null,
      fromDate1: "",
      toDate1: "",
      fromDate2: "",
      toDate2: "",
      sortBy: {
        column: null,
        order: "asc" // asc or desc
      }
    };
  },
  computed: {
    url() {
      return process.env.VUE_APP_NKRSYNC_BE;
    },
    token() {
      return this.$store.getters["auth/currentToken"];
    }
  },
  methods: {
    compareThisMonthWithPreviousMonth() {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const thisMonthStart = new Date(currentYear, currentMonth, 1);
      const thisMonthEnd = new Date(currentYear, currentMonth + 1, 0);

      const previousMonthStart = new Date(currentYear, currentMonth - 1, 1);
      const previousMonthEnd = new Date(currentYear, currentMonth, 0);

      this.fromDate1 = this.formatDateWithTime(thisMonthStart);
      this.toDate1 = this.formatDateWithTime(thisMonthEnd, true);
      this.fromDate2 = this.formatDateWithTime(previousMonthStart);
      this.toDate2 = this.formatDateWithTime(previousMonthEnd, true);

      this.loadComparison();
    },

    compareThisMonthWithLastYear() {
      const now = new Date();
      const currentMonth = now.getMonth();
      const currentYear = now.getFullYear();

      const thisMonthStart = new Date(currentYear, currentMonth, 1);
      const thisMonthEnd = new Date(currentYear, currentMonth + 1, 0);

      const lastYearMonthStart = new Date(currentYear - 1, currentMonth, 1);
      const lastYearMonthEnd = new Date(currentYear - 1, currentMonth + 1, 0);

      this.fromDate1 = this.formatDateWithTime(thisMonthStart);
      this.toDate1 = this.formatDateWithTime(thisMonthEnd, true);
      this.fromDate2 = this.formatDateWithTime(lastYearMonthStart);
      this.toDate2 = this.formatDateWithTime(lastYearMonthEnd, true);

      this.loadComparison();
    },

    compareLastMonthWithPreviousYear() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const lastMonth = now.getMonth() - 1;

      const lastMonthStart = new Date(currentYear, lastMonth, 1);
      const lastMonthEnd = new Date(currentYear, lastMonth + 1, 0);

      const previousYearMonthStart = new Date(currentYear - 1, lastMonth, 1);
      const previousYearMonthEnd = new Date(currentYear - 1, lastMonth + 1, 0);

      this.fromDate1 = this.formatDateWithTime(lastMonthStart);
      this.toDate1 = this.formatDateWithTime(lastMonthEnd, true);
      this.fromDate2 = this.formatDateWithTime(previousYearMonthStart);
      this.toDate2 = this.formatDateWithTime(previousYearMonthEnd, true);

      this.loadComparison();
    },

    compareLast6MonthsWithPrevious6() {
      const now = new Date();
      const currentYear = now.getFullYear();
      const currentMonth = now.getMonth();

      const last6MonthsStart = new Date(
        currentYear,
        currentMonth - 6,
        now.getDate()
      );
      const last6MonthsEnd = new Date(now);

      const previous6MonthsEnd = new Date(
        currentYear,
        currentMonth - 6,
        new Date(currentYear, currentMonth - 6, 0).getDate()
      );
      const previous6MonthsStart = new Date(
        currentYear,
        currentMonth - 12,
        now.getDate()
      );

      this.fromDate1 = this.formatDateWithTime(last6MonthsStart);
      this.toDate1 = this.formatDateWithTime(last6MonthsEnd, true);
      this.fromDate2 = this.formatDateWithTime(previous6MonthsStart);
      this.toDate2 = this.formatDateWithTime(previous6MonthsEnd, true);

      this.loadComparison();
    },

    compareThisYearWithLastYear() {
      const now = new Date();
      const currentYear = now.getFullYear();

      this.fromDate1 = this.formatDateWithTime(new Date(currentYear, 0, 1));
      this.toDate1 = this.formatDateWithTime(
        new Date(currentYear, 11, 31),
        true
      );
      this.fromDate2 = this.formatDateWithTime(new Date(currentYear - 1, 0, 1));
      this.toDate2 = this.formatDateWithTime(
        new Date(currentYear - 1, 11, 31),
        true
      );

      this.loadComparison();
    },

    loadComparison() {
      if (
        !this.fromDate1 ||
        !this.toDate1 ||
        !this.fromDate2 ||
        !this.toDate2
      ) {
        alert("Please fill in all date fields");
        return;
      }

      this.loading = true;
      const fromDate1 = this.formatDateTimeString(this.fromDate1);
      const toDate1 = this.formatDateTimeString(this.toDate1, true);
      const fromDate2 = this.formatDateTimeString(this.fromDate2);
      const toDate2 = this.formatDateTimeString(this.toDate2, true);

      getProductsStatsCompare(fromDate1, toDate1, fromDate2, toDate2)
        .then(response => {
          this.comparisonData = response;
        })
        .catch(error => {
          console.error("Error loading comparison:", error);
          alert("Error loading comparison data");
        })
        .finally(() => {
          this.loading = false;
        });
    },

    formatDate(date) {
      return date.toISOString().split("T")[0];
    },

    formatDateWithTime(date, isEndOfDay = false) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      const time = isEndOfDay ? "23:59:59" : "00:00:00";
      return `${year}-${month}-${day} ${time}`;
    },

    formatDateTimeString(dateString, isEndOfDay = false) {
      if (isEndOfDay) {
        return `${dateString} 23:59:59`;
      }
      return `${dateString} 00:00:00`;
    },

    sortTable(column) {
      if (this.sortBy.column === column) {
        this.sortBy.order = this.sortBy.order === "asc" ? "desc" : "asc";
      } else {
        this.sortBy.column = column;
        this.sortBy.order = "asc";
      }
    },

    getSortedData() {
      if (!this.comparisonData || !this.comparisonData.comparison) {
        return [];
      }

      const data = [...this.comparisonData.comparison];

      if (!this.sortBy.column) {
        return data;
      }

      data.sort((a, b) => {
        let aValue = a[this.sortBy.column];
        let bValue = b[this.sortBy.column];

        // Handle numeric comparisons
        if (typeof aValue === "number" && typeof bValue === "number") {
          return this.sortBy.order === "asc"
            ? aValue - bValue
            : bValue - aValue;
        }

        // Handle string comparisons
        aValue = String(aValue).toLowerCase();
        bValue = String(bValue).toLowerCase();

        if (this.sortBy.order === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      });

      return data;
    }
  }
};
</script>

<style scoped>
.compare-container {
  width: 100%;
}

.btn {
  padding: 0.5rem 1rem;
  border: 1px solid #ccc;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  transition: all 0.3s ease;
}

.btn-secondary {
  background-color: #f0f0f0;
  color: #333;
}

.btn-secondary:hover {
  background-color: #e0e0e0;
}

.btn-primary {
  background-color: #007bff;
  color: white;
}

.btn-primary:hover {
  background-color: #0056b3;
}

.btn-export {
  padding: 0.5rem 1rem;
  background-color: #333;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 0.9rem;
  text-decoration: none;
  display: inline-block;
  transition: background-color 0.3s ease;
}

.btn-export:hover {
  background-color: #555;
}

.preset-options {
  background-color: #f9f9f9;
  padding: 1rem;
  border-radius: 4px;
}

.custom-ranges {
  background-color: #f9f9f9;
  padding: 1rem;
  border-radius: 4px;
}

input[type="date"] {
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

.grid {
  display: grid;
}

.grid-cols-2 {
  grid-template-columns: repeat(2, minmax(0, 1fr));
}

.gap-4 {
  gap: 1rem;
}

.gap-6 {
  gap: 1.5rem;
}

.mb-6 {
  margin-bottom: 1.5rem;
}

.summary-cards {
  margin-bottom: 2rem;
}

.summary-card {
  padding: 1.5rem;
  border-radius: 8px;
  border: 1px solid #ddd;
  background-color: #fff;
}

.summary-card.period-1 {
  border-left: 4px solid #007bff;
}

.summary-card.period-2 {
  border-left: 4px solid #6c757d;
}

.table-responsive {
  overflow-x: auto;
  margin-top: 2rem;
  max-height: 600px;
  overflow-y: auto;
}

.comparison-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.9rem;
}

.comparison-table thead {
  background-color: #f8f9fa;
  font-weight: 600;
  position: sticky;
  top: 0;
  z-index: 10;
}

.comparison-table th {
  padding: 1rem;
  text-align: left;
  border-bottom: 2px solid #dee2e6;
  background-color: #f8f9fa;
  font-weight: 600;
  user-select: none;
}

.sortable {
  cursor: pointer;
  transition: background-color 0.2s ease;
}

.sortable:hover {
  background-color: #e9ecef;
}

.sort-indicator {
  margin-left: 0.5rem;
  font-size: 0.8rem;
}

.comparison-table td {
  padding: 0.75rem 1rem;
  border-bottom: 1px solid #dee2e6;
}

.comparison-table tbody tr:hover {
  background-color: #f9f9f9;
}

.product-code {
  font-weight: 600;
  color: #007bff;
}

.description {
  max-width: 400px;
  word-wrap: break-word;
}

.number {
  text-align: center;
  font-weight: 500;
}

.change {
  font-weight: 600;
}

.percentage {
  font-weight: 600;
  text-align: center;
  padding: 0.5rem;
  border-radius: 4px;
}

.row-up {
  background-color: #f0fdf4;
}

.row-down {
  background-color: #fef2f2;
}
</style>
